import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, ShieldCheck, Crosshair, Check, ArrowRight, Repeat, Loader2, CreditCard } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Overline } from "../Reveal";

const useCheckout = () => {
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);
  const checkout = async (lookup_key) => {
    if (!user) {
      toast.info("Inicia sesión para asociar la compra a tu cuenta.");
      window.location.href = "/acceso";
      return;
    }
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo iniciar el pago");
      setLoadingKey(null);
    }
  };
  return { loadingKey, checkout };
};

const tiers = [
  {
    icon: Search,
    tag: "Simple",
    name: "Escaneo de exposición",
    price: "349 € – 490 €",
    note: "Pago único · IVA no incluido",
    color: "#22e07a",
    lookup_key: "cyvr_simple",
    delivery: "Entrega en 3-5 días laborables",
    ideal: "Empresas que nunca han hecho una auditoría y quieren una primera foto de su exposición.",
    features: [
      "Escaneo automatizado de puertos y servicios expuestos",
      "Detección de vulnerabilidades conocidas (CVE)",
      "Comprobación de certificados SSL/TLS y config. básicas",
      "Informe con hallazgos priorizados por severidad",
    ],
  },
  {
    icon: ShieldCheck,
    tag: "Medio",
    name: "Análisis validado",
    price: "1.200 € – 1.800 €",
    note: "Pago único · IVA no incluido",
    color: "#00f0ff",
    featured: true,
    delivery: "1 ronda de retesting incluida",
    ideal: "Infraestructura en producción que necesita un diagnóstico fiable, no solo un escaneo automático.",
    features: [
      "Todo lo del nivel Simple + red interna y perímetro externo",
      "Validación manual (descarta falsos positivos)",
      "Revisión de firewall y accesos remotos (VPN, RDP…)",
      "Informe técnico + informe ejecutivo para dirección",
    ],
  },
  {
    icon: Crosshair,
    tag: "Experto",
    name: "Pentest de intrusión",
    price: "2.900 € – 4.500 €",
    note: "Pago único · IVA no incluido",
    color: "#b026ff",
    delivery: "Retest + soporte durante la remediación",
    ideal: "Datos sensibles o cumplimiento (RGPD, NIS2, ENS), o empresas ya atacadas.",
    features: [
      "Todo lo del nivel Medio + explotación controlada real",
      "Escalada de privilegios y movimiento lateral",
      "Evidencias reproducibles + plan de remediación",
      "Sesión de presentación con tu equipo técnico",
    ],
  },
];

const Card = ({ t, i, onCheckout, loadingKey }) => {
  const Icon = t.icon;
  const loading = loadingKey === t.lookup_key;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      data-testid={`pricing-tier-${i}`}
      className={`relative flex flex-col rounded-2xl border p-8 transition-colors duration-500 ${
        t.featured
          ? "border-cyber-cyan/40 bg-cyber-cyan/[0.06]"
          : "border-white/10 bg-white/[0.03] hover:border-white/25"
      }`}
    >
      {t.featured && (
        <span className="absolute -top-3 left-8 rounded-full bg-cyber-cyan px-3 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-[#04060a]">
          Recomendado
        </span>
      )}
      <div className="flex items-center justify-between">
        <span
          className="flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ background: `${t.color}1a`, border: `1px solid ${t.color}55` }}
        >
          <Icon className="h-6 w-6" style={{ color: t.color }} strokeWidth={1.6} />
        </span>
        <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/40">{t.tag}</span>
      </div>

      <h3 className="mt-6 font-head text-xl font-bold text-white">{t.name}</h3>
      <p className="mt-4 font-head text-3xl font-black tracking-tight text-white">{t.price}</p>
      <p className="mt-1 font-mono-tech text-[11px] uppercase tracking-wider text-white/40">{t.note}</p>

      <ul className="mt-6 flex-1 space-y-3">
        {t.features.map((f) => (
          <li key={f} className="flex items-start gap-3 font-sans text-sm text-white/70">
            <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: t.color }} />
            {f}
          </li>
        ))}
      </ul>

      <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <p className="font-mono-tech text-[10px] uppercase tracking-wider" style={{ color: t.color }}>{t.delivery}</p>
        <p className="mt-2 font-sans text-xs leading-relaxed text-white/50"><span className="text-white/70">Ideal para:</span> {t.ideal}</p>
      </div>

      {t.lookup_key ? (
        <button
          onClick={() => onCheckout(t.lookup_key)}
          disabled={loading}
          data-testid={`pricing-cta-${i}`}
          className={`group mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 ${
            t.featured ? "bg-cyber-cyan text-[#04060a]" : "bg-cyber-cyan text-[#04060a]"
          }`}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> Pagar y reservar</>}
        </button>
      ) : (
        <a
          href="#contacto"
          data-testid={`pricing-cta-${i}`}
          className={`group mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:scale-[1.02] ${
            t.featured ? "bg-cyber-cyan text-[#04060a]" : "border border-white/15 text-white/80"
          }`}
        >
          Solicitar presupuesto
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      )}
    </motion.div>
  );
};

export const Pricing = () => {
  const { loadingKey, checkout } = useCheckout();
  return (
  <section
    id="precios"
    className="relative mx-auto max-w-7xl px-5 py-28 md:px-10 md:py-40"
    data-testid="pricing"
  >
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <Overline>Tarifas</Overline>
        <h2 className="mt-6 max-w-2xl font-head text-4xl font-bold tracking-tight text-white md:text-5xl">
          Análisis de puertos y vulnerabilidades
        </h2>
      </div>
      <p className="max-w-xs font-sans text-sm text-white/50">
        Tres niveles según la madurez de tu empresa. Precios cerrados, sin sorpresas. Presupuesto ajustado tras una llamada inicial.
      </p>
    </div>

    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {tiers.map((t, i) => (
        <Card key={t.tag} t={t} i={i} onCheckout={checkout} loadingKey={loadingKey} />
      ))}
    </div>

    {/* Recurring option */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      data-testid="pricing-recurring"
      className="glass mt-8 flex flex-col items-start justify-between gap-6 rounded-2xl p-8 md:flex-row md:items-center"
    >
      <div className="flex items-center gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-cyber-violet/15 text-cyber-violet" style={{ border: "1px solid #b026ff55" }}>
          <Repeat className="h-6 w-6" strokeWidth={1.6} />
        </span>
        <div>
          <h3 className="font-head text-lg font-bold text-white">
            Vigilancia continua · desde <span className="text-cyber-cyan">79 €/mes</span>
          </h3>
          <p className="mt-1 font-sans text-sm text-white/55">
            Escaneo automatizado mensual con alertas. Complemento a cualquier nivel — encaja con el SOC 24/7.
          </p>
        </div>
      </div>
      <button
        onClick={() => checkout("cyvr_monitor_monthly")}
        disabled={loadingKey === "cyvr_monitor_monthly"}
        data-testid="pricing-recurring-cta"
        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-cyber-cyan px-6 py-3 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
      >
        {loadingKey === "cyvr_monitor_monthly" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Suscribirme · 79 €/mes <ArrowRight className="h-4 w-4" /></>}
      </button>
    </motion.div>
  </section>
  );
};
